import { useSelector } from 'react-redux';
import DialogModal from '../../components/DialogModal';
import { ScoringTypeModalProps } from './types/scoringType';
import { RootState } from '../../redux/store';
import { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, TextField } from '@mui/material';
import MultiValueInput from '../../components/MultiValueInput';
import scoringTypes from '../../apis/scoringTypes';
import { StyledButton } from '../Exams/styled';

const EditScoringTypeModal: React.FC<ScoringTypeModalProps> = ({
  fetchData,
  open,
  onClose,
  scoringType,
  onApiSuccessAndFailure
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState<string>(scoringType?.name || '');
  const [validScores, setValidScores] = useState<string[]>(
    scoringType?.validScores || []
  );

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  useEffect(() => {
    setName(scoringType?.name || '');
    setValidScores(scoringType?.validScores || []);
  }, [scoringType]);

  const handleAddScore = useCallback((score: string) => {
    setValidScores((prev) => [...prev, score]);
  }, []);

  const handleRemoveScore = useCallback((indexToRemove: number) => {
    setValidScores((prev) =>
      prev?.filter((_, index) => index !== indexToRemove)
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (!name || !validScores) {
        return;
      }
      const payload = {
        name,
        validScores: validScores.length === 0 ? null : validScores,
        id: scoringType?.id
      };
      const response = await scoringTypes.upsertScoringType(
        loggedInUser.school,
        payload
      );
      if (response) {
        fetchData();
        const message = `Scoring type updated successfully`;
        onApiSuccessAndFailure(true, message);
      }
    } catch (error: any) {
      onApiSuccessAndFailure(
        false,
        'Scoring type with this name already exists'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    name,
    validScores,
    isSubmitting,
    loggedInUser.school,
    onApiSuccessAndFailure,
    scoringType?.id,
    fetchData
  ]);

  return (
    <>
      <DialogModal
        open={open}
        onClose={onClose}
        title='Edit Scoring Type'
        actions={
          <>
            <Button onClick={onClose} color='primary' variant='contained'>
              Cancel
            </Button>
            <StyledButton
              onClick={handleSubmit}
              disabled={isSubmitting || !name.trim()}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Save'
              )}
            </StyledButton>
          </>
        }
      >
        <Box>
          <TextField
            fullWidth
            id='name'
            name='name'
            label='Scoring Type Name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          <Box marginTop={2}>
            <MultiValueInput
              values={validScores || []}
              onAddValue={handleAddScore}
              onRemoveValue={handleRemoveScore}
              placeholder='Add valid score and press enter'
              label='Valid Scores'
              validationType='text'
            />
          </Box>
        </Box>
      </DialogModal>
    </>
  );
};

export default EditScoringTypeModal;
